import React, { useState, useRef, useEffect } from "react";
import { KpiModal, ModalHeader } from "./KpiIcons";

/* ── Location multi-select dropdown ── */
const LocationMultiSelect = ({ locations, selected = [], onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const toggle = (id) =>
        onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

    const toggleAll = () =>
        onChange(selected.length === locations.length ? [] : locations.map((l) => l.id));

    const allSelected = locations.length > 0 && selected.length === locations.length;

    const label = selected.length === 0
        ? "All Locations"
        : selected.length === locations.length
            ? "All Locations"
            : selected.length === 1
                ? locations.find((l) => l.id === selected[0])?.name || "1 selected"
                : `${selected.length} locations selected`;

    return (
        <div ref={ref} style={{ position: "relative" }}>
            {/* Trigger */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="kpi-input"
                style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    textAlign: "left",
                    background: selected.length ? "var(--accent-light, #eff6ff)" : "",
                    borderColor: selected.length ? "var(--accent, #2563eb)" : "",
                    color: selected.length ? "var(--accent, #2563eb)" : "",
                    fontWeight: selected.length ? 600 : 400,
                }}
            >
                <span>{label}</span>
                <span style={{ fontSize: 9, opacity: 0.5, marginLeft: 8 }}>{open ? "▲" : "▾"}</span>
            </button>

            {/* Dropdown */}
            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                    zIndex: 999, background: "#fff",
                    border: "1px solid var(--rule, #e2e8f0)",
                    borderRadius: 10,
                    boxShadow: "0 12px 32px rgba(0,0,0,.13)",
                    maxHeight: 220, overflowY: "auto",
                    padding: "4px 0",
                }}>
                    {/* Select all */}
                    <label style={{
                        display: "flex", gap: 8, padding: "8px 13px", cursor: "pointer",
                        fontSize: 12, fontWeight: 700,
                        borderBottom: "1px solid var(--rule, #e2e8f0)",
                        background: allSelected ? "var(--accent-light, #eff6ff)" : "transparent",
                        alignItems: "center",
                    }}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }}
                        />
                        All Locations
                    </label>

                    {locations.map((loc) => {
                        const checked = selected.includes(loc.id);
                        return (
                            <label key={loc.id} style={{
                                display: "flex", gap: 8, padding: "7px 13px", cursor: "pointer",
                                fontSize: 12,
                                background: checked ? "var(--accent-light, #eff6ff)" : "transparent",
                                alignItems: "center",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggle(loc.id)}
                                    style={{ accentColor: "var(--accent, #2563eb)", width: 13, height: 13 }}
                                />
                                <span style={{ fontWeight: 600, color: "var(--ink-2, #334155)" }}>{loc.name}</span>
                            </label>
                        );
                    })}
                </div>
            )}

           
            {selected.length > 0 && selected.length < locations.length && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
                    {selected.map((id) => {
                        const loc = locations.find((l) => l.id === id);
                        return (
                            <span key={id} style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "2px 8px 2px 10px", borderRadius: 20,
                                background: "var(--accent-light, #eff6ff)",
                                border: "1px solid var(--accent-border, #bfdbfe)",
                                fontSize: 11.5, fontWeight: 600,
                                color: "var(--accent, #2563eb)",
                            }}>
                                {loc?.name || `#${id}`}
                                <button
                                    onClick={() => toggle(id)}
                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: "#93c5fd", fontSize: 14, lineHeight: 1, padding: 0 }}
                                >×</button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ── Main modal ── */
const KpiFiltersModal = ({
    show,
    onHide,
    filters = {},
    onChange,
    onApply,
    onReset,
    locations = [],
}) => {
    const activeCount = Object.values(filters).filter((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v))).length;

    const handle = (key, value) => onChange?.(key, value);

    const Section = ({ label, children }) => (
        <div>
            <label className="kpi-label" style={{ marginBottom: 10 }}>{label}</label>
            {children}
        </div>
    );

    const PillGroup = ({ options, active, onSelect }) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {options.map(({ value, label, icon }) => (
                <button
                    key={value}
                    onClick={() => onSelect(active === value ? "" : value)}
                    className={`kpi-filter-pill${active === value ? " active" : ""}`}
                >
                    {icon && <span>{icon} </span>}
                    {label}
                </button>
            ))}
        </div>
    );

    return (
        <KpiModal show={show} onClose={onHide} maxWidth={520}>
            <ModalHeader
                emoji="🎛️"
                title={
                    <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        Advanced Filters
                        {activeCount > 0 && (
                            <span style={{
                                padding: "2px 8px",
                                background: "var(--accent)",
                                color: "#fff",
                                borderRadius: 20,
                                fontSize: 10,
                                fontWeight: 700,
                            }}>
                                {activeCount} active
                            </span>
                        )}
                    </span>
                }
                onClose={onHide}
            />

            <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 22, overflowY: "auto" }}>

                {/* ── Locations ── */}
                {locations.length > 0 && (
                    <Section label="Location (Unit Code)">
                        <LocationMultiSelect
                            locations={locations}
                            selected={filters.location_ids || []}
                            onChange={(v) => handle("location_ids", v)}
                        />
                    </Section>
                )}

                {/* ── Status ── */}
                <Section label="Status">
                    <PillGroup
                        active={filters.status || ""}
                        onSelect={(v) => handle("status", v)}
                        options={[
                            { value: "", label: "All" },
                            { value: "pending", label: "Pending", icon: "⏳" },
                            { value: "completed", label: "Completed", icon: "✅" },
                        ]}
                    />
                </Section>

                {/* ── Date Range ── */}
                <Section label="Date Range">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                            <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
                                From
                            </div>
                            <input
                                type="date"
                                className="kpi-input"
                                value={filters.from_date || ""}
                                onChange={(e) => handle("from_date", e.target.value)}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
                                To
                            </div>
                            <input
                                type="date"
                                className="kpi-input"
                                value={filters.to_date || ""}
                                onChange={(e) => handle("to_date", e.target.value)}
                            />
                        </div>
                    </div>
                </Section>

                {/* ── Footer ── */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 16,
                    borderTop: "1px solid var(--rule)",
                }}>
                    <button
                        className="kpi-btn-secondary"
                        onClick={() => { onReset?.(); onHide(); }}
                    >
                        Reset All
                    </button>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button className="kpi-btn-secondary" onClick={onHide}>
                            Cancel
                        </button>
                        <button
                            className="kpi-btn-primary"
                            onClick={() => { onApply?.(); onHide(); }}
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </KpiModal>
    );
};

export default KpiFiltersModal;